<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-select
              v-model="selectedArea"
              :loading="loadingAreas"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>

          <b-field>
            <b-select
              v-model="selectedCatalog"
              :loading="loadingAreas"
              :disabled="catalogs.length <= 0"
              :placeholder="
                $t('message.please_select') + $t('attributes.catalog')
              "
              @input="onSelectCatalog"
            >
              <option value="">全部類別</option>
              <option
                v-for="option in catalogs"
                :value="option"
                :key="option.catalog_no"
              >
                {{ !option.status ? '[下架]' : '' }}
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.enabled') }}</option>
              <option value="false">{{ $t('attributes.disabled') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="searchTitle"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.travel_info') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      style="max-width: 1250px; margin: 0 auto;"
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ activeStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        :label="$t('attributes.picture')"
        width="100"
        sortable
        centered
        v-slot="props"
      >
        <figure class="image">
          <img
            :src="CatalogClassImage(props.row.picture)"
            :alt="props.row.picture_alt"
            style="width: 50px; margin: auto;"
          />
        </figure>
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        width="200"
        v-slot="props"
      >
        <p v-html="props.row.title"></p>
      </b-table-column>

      <b-table-column
        field="catalog"
        :label="$t('attributes.catalog')"
        sortable
        width="360"
        v-slot="props"
      >
        <p
          :title="props.row.catalog_names"
          v-html="
            props.row.catalog_names.length > 98
              ? props.row.catalog_names.substring(0, 98) + '......'
              : props.row.catalog_names
          "
        ></p>
      </b-table-column>

      <b-table-column
        field="url"
        :label="$t('attributes.url')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <a
          v-if="props.row.url"
          :href="props.row.url"
          :title="props.row.url"
          target="_blank"
        >
          連結
        </a>
      </b-table-column>

      <b-table-column
        field="updateAt"
        :label="$t('attributes.gift_updated_at')"
        width="130"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDateTime(props.row.updated_at) }}
      </b-table-column>

      <b-table-column :width="140" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button type="is-danger" @click.prevent="onDelete(props.row)">
            {{ $t('attributes.delete') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1600"
      has-modal-card
    >
      <travel-info-form :travel-info="selectedItem" @send="onSend" />
    </b-modal>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import { AREA_API, CATALOG_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import TravelInfoForm from './TravelInfoForm'
import debounce from 'lodash/debounce'

export default {
  name: 'TravelInfoList',
  components: { TravelInfoForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      searchTitle: this.query.title || '',
      filterStatus: this.query.status || true,
      sortField: this.query.sort || 'updateAt',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'desc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      loadingAreas: false,
      areas: [],
      catalogs: [],
      selectedArea: null,
      selectedAreaCode: this.query.ac || null,
      selectedCatalog: null,
      selectedCatalogNo: this.query.cn || null,

      isFormModalActive: false,
      sending: false,

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingAreas || this.loadingItems
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.catalogs = []
        this.selectedArea = null
        this.selectedCatalog = null

        if (this.selectedAreaCode) {
          this.selectedArea = areas.find(a => a.code === this.selectedAreaCode)
          this.catalogs = this.selectedArea
            ? this.selectedArea.catalogs.sort(c => c.sort)
            : []
        }
        if (this.selectedCatalogNo) {
          this.selectedCatalog = this.catalogs.find(
            c => c.catalog_no === this.selectedCatalogNo
          )
        }

        if (this.selectedArea) {
          await this.loadItems()
        }
      } catch (e) {
        this.areas = []
        this.catalogs = []
        this.selectedArea = null
        this.selectedAreaCode = null
        this.selectedCatalog = null
        this.selectedCatalogNo = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    async loadItems() {
      try {
        const api = `${CATALOG_API}/travel-infos`
        const params = {
          area_code: this.selectedAreaCode,
          catalog_no: this.selectedCatalogNo,
          active: this.filterStatus,
          search: this.searchTitle,
          sort_by: this.sortField,
          by_descending: this.sortOrder === 'desc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('travel-info', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterStatus = query.status || true
      this.searchTitle = query.title || ''
      this.sortField = query.sort || 'updateAt'
      this.sortOrder = query.order || 'desc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10

      this.selectedArea = null
      this.selectedCatalog = null
      this.selectedAreaCode = query.ac || null
      this.selectedCatalogNo = query.cn || null

      if (this.selectedAreaCode) {
        this.selectedArea = this.areas.find(
          a => a.code === this.selectedAreaCode
        )
        this.catalogs = this.selectedArea
          ? this.selectedArea.catalogs.sort(c => c.sort)
          : []
      }
      if (this.selectedCatalogNo) {
        this.selectedCatalog = this.catalogs.find(
          c => c.catalog_no === this.selectedCatalogNo
        )
      }
    },
    updateQuery() {
      const query = {
        status: this.filterStatus,
        title: this.searchTitle,
        ac: this.selectedAreaCode,
        cn: this.selectedCatalogNo,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.updateQuery()
    },
    onFilter: debounce(function() {
      this.updateQuery()
      this.loadItems()
    }, 500),
    onSelectArea(option) {
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null
      this.catalogs = option ? option.catalogs.sort(c => c.sort) : []
      this.selectedCatalog = null
      this.selectedCatalogNo = null

      this.updateQuery()
    },
    onSelectCatalog(option) {
      this.selectedCatalog = option
      this.selectedCatalogNo = option ? option.catalog_no : null

      this.updateQuery()
    },
    onPageChange(page) {
      this.page = page

      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
    },

    onAdd() {
      this.selectedItem = {
        id: null,
        sort: 1,
        status: true,
        title: '',
        picture: null,
        url: '',
        catalogs: null
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isFormModalActive = true
    },
    onDelete(item) {
      this.$buefy.dialog.confirm({
        message: this.$t('attributes.confirm_delete'),
        onConfirm: () => {
          this.deleteItem(item)
        }
      })
    },
    onSend(item) {
      if (item.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        const api = `${CATALOG_API}/travel-infos`

        this.sending = true
        await this.axios.post(api, item)
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${CATALOG_API}/travel-infos`

        this.sending = true
        await this.axios.put(api, item)
        await this.loadItems()
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    async deleteItem(item) {
      try {
        const api = `${CATALOG_API}/travel-infos/${item.id}`

        this.sending = true
        await this.axios.delete(api)
        notifyService.success(this.$t('attributes.delete_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.sending = false
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = this.CatalogBannerImage(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
